import { useMemo, useState } from 'react';
import axios from 'axios';
import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	FormInput,
	Modal,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
} from 'carrier-fe';
import {
	ApplianceAttributes,
	ApplianceVariant,
} from '../../../types/appliance-management';

type Props = {
	applianceId: string;
	variants: ApplianceVariant[];
	attributes: ApplianceAttributes | null;
	canEdit: boolean;
	onChanged: () => void;
};

const emptyForm = {
	serial_prefix: '',
	installation_type: '',
	gas_safe_model: '',
	gas_safe_version: '',
	system_type: '',
	size: '',
};

type FormValues = typeof emptyForm;

/**
 * CUS-934's "List of prefixes for this product", and the home of the five attributes CUS-933 and CUS-934
 * place on the appliance.
 *
 * They belong here because they vary between the prefixes of one appliance: in production, size differs
 * within an appliance name for 27 of 48 appliances, Gas Safe version for 7, type for 3 and installation
 * type for 2. Vitodens 100-W has 86 prefixes over 19 distinct combinations, which is also why this is a
 * scrolling table with its own filter rather than a list of cards.
 */
function SerialPrefixesSection({
	applianceId,
	variants,
	attributes,
	canEdit,
	onChanged,
}: Props) {
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const [filter, setFilter] = useState<string>('');
	const [formOpen, setFormOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<ApplianceVariant | null>(null);
	const [deleting, setDeleting] = useState<ApplianceVariant | null>(null);
	const [values, setValues] = useState<FormValues>(emptyForm);
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	const canAdd =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_VARIANT_STORE
		);
	const canUpdate =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_VARIANT_UPDATE
		);
	const canDelete =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_VARIANT_DELETE
		);

	const filtered = useMemo(() => {
		const needle = filter.trim().toLowerCase();

		if (!needle) return variants;

		return variants.filter((variant) =>
			[
				variant.serial_prefix,
				variant.installation_type_display,
				variant.gas_safe_model,
				variant.gas_safe_version,
				variant.system_type,
				variant.size,
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(needle))
		);
	}, [variants, filter]);

	const openCreate = () => {
		setEditing(null);
		setValues(emptyForm);
		setErrors({});
		setFormOpen(true);
	};

	const openEdit = (variant: ApplianceVariant) => {
		setEditing(variant);
		setValues({
			serial_prefix: variant.serial_prefix ?? '',
			installation_type: variant.installation_type ?? '',
			gas_safe_model: variant.gas_safe_model ?? '',
			gas_safe_version: variant.gas_safe_version ?? '',
			system_type: variant.system_type ?? '',
			size: variant.size ?? '',
		});
		setErrors({});
		setFormOpen(true);
	};

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
		setErrors({});
	};

	const save = async () => {
		setDisabled(true);
		setErrors({});

		try {
			const { data } = editing
				? await axios.patch(
						route(
							API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.VARIANT
								.UPDATE,
							{
								viessmannAppliance: applianceId,
								variant: editing.id,
							}
						),
						values
				  )
				: await axios.post(
						route(
							API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.VARIANT
								.STORE,
							{ viessmannAppliance: applianceId }
						),
						values
				  );

			customToast({
				title: data?.message || 'Serial prefix saved.',
				variant: Variants.Success,
			});

			onChanged();
			closeForm();
		} catch (err: any) {
			setErrors(err?.response?.data?.data?.errors ?? {});

			customToast({
				title:
					err?.response?.data?.message ||
					'Error saving the serial prefix.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const remove = async () => {
		if (!deleting) return;

		setDisabled(true);

		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.VARIANT.DELETE, {
					viessmannAppliance: applianceId,
					variant: deleting.id,
				})
			);

			customToast({
				title: data?.message || 'Serial prefix deleted.',
				variant: Variants.Success,
			});

			onChanged();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error deleting the serial prefix.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setDeleting(null);
		}
	};

	/*
	 * A native <select> rather than the carrier-fe Dropdown: the table is its own scroll container
	 * (overflow clips absolutely-positioned menus), and the Bootstrap dropdown menu would be hidden
	 * inside it - worst for the top row. This mirrors how the shared DataTable renders row actions.
	 *
	 * The same for every row, so it is built once rather than per row.
	 */
	const rowActionOptions = useMemo(() => {
		const options: { label: string; value: string }[] = [];

		if (canUpdate) {
			options.push({
				label: crud?.select_labels?.edit || 'Edit',
				value: 'edit',
			});
		}

		if (canDelete) {
			options.push({
				label: crud?.select_labels?.delete || 'Delete',
				value: 'delete',
			});
		}

		return options;
	}, [canUpdate, canDelete, crud]);

	const handleRowAction = (variant: ApplianceVariant, value: string) => {
		if (value === 'edit') openEdit(variant);
		if (value === 'delete') setDeleting(variant);
	};

	const actionsLabel = StringHelpers.title(
		crud?.buttons?.actions?.default || 'Actions'
	);
	const selectLabel = StringHelpers.title(fields?.select || 'Select');

	return (
		<>
			<Card className="mb-4">
				<CardBody>
					<div className="d-flex justify-content-between align-items-start mb-3">
						<div>
							<SubTitle
								title={StringHelpers.title(
									crud?.sub_titles?.serial_prefixes ||
										'Serial Prefixes'
								)}
								style={{ color: '#464C5E' }}
							/>
							<div className="text-muted">
								{replaceKeyWithValue(
									fields?.serial_prefixes_description ||
										'A serial prefix identifies this appliance when an installer looks up a serial number. Gas Safe model, version, type and size belong to the prefix, because they differ between prefixes of the same appliance. :count in total.',
									'count',
									`${variants.length} ${
										variants.length === 1
											? 'prefix'
											: 'prefixes'
									}`
								)}
							</div>
						</div>
						{canAdd && (
							<Button
								label={StringHelpers.title(
									crud?.buttons?.create?.default || 'Create'
								)}
								onClick={openCreate}
								variant={Variants.Dark}
								type={ButtonTypes.Outline}
								disabled={disabled}
							/>
						)}
					</div>

					{variants.length > 0 && (
						<div style={{ maxWidth: '320px' }}>
							<FormInput
								type="text"
								name="prefix_filter"
								label={StringHelpers.title(
									fields?.filter_prefixes || 'filter prefixes'
								)}
								placeholder={StringHelpers.title(
									fields?.search || 'search'
								)}
								value={filter}
								onChange={(value) => setFilter(value as string)}
							/>
						</div>
					)}

					{variants.length === 0 ? (
						<p className="mb-0">
							{fields?.no_serial_prefixes ||
								'This appliance has no serial prefixes, so no serial number will resolve to it.'}
						</p>
					) : (
						/*
						 * Its own scroll container. Vitodens 100-W has 86 rows, and letting the page grow to
						 * fit them pushes the warranty options and the audit log out of reach.
						 */
						<div
							className="table-responsive"
							style={{ maxHeight: '420px', overflowY: 'auto' }}
						>
							<table className="table w-100 mb-0">
								<thead>
									<tr>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.serial_prefix ||
													'Serial Prefix'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.installation_type ||
													'Installation Type'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.gas_safe_model ||
													'Gas Safe Model'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.gas_safe_version ||
													'Gas Safe Version'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.type || 'Type'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.size || 'Size'
											)}
										</th>
										<th className="border border-gray-300 px-3 py-2">
											{StringHelpers.title(
												fields?.source || 'Source'
											)}
										</th>
										{(canUpdate || canDelete) && (
											<th className="border border-gray-300 px-3 py-2">
												{StringHelpers.title(
													fields?.actions || 'Actions'
												)}
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{filtered.length === 0 ? (
										<tr>
											<td
												colSpan={8}
												className="border border-gray-300 px-3 py-2"
											>
												No serial prefixes match that filter.
											</td>
										</tr>
									) : (
										filtered.map((variant) => (
											<tr key={variant.id}>
												<td className="border border-gray-300 px-3 py-2">
													<span
														className="fw-bold"
														/*
														 * source_order is why one of two appliances sharing
														 * a prefix wins the serial lookup, so it is worth
														 * being able to see it.
														 */
														title={`${
															fields?.source_order ||
															'File order'
														}: ${
															variant.source_order ??
															'-'
														}`}
													>
														{variant.serial_prefix}
													</span>
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.installation_type_display ||
														'-'}
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.gas_safe_model ||
														'-'}
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.gas_safe_version ||
														'-'}
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.system_type || '-'}
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.size || '-'}
												</td>
												<td className="border border-gray-300 px-3 py-2">
													{variant.source_display || '-'}
												</td>
												{(canUpdate || canDelete) && (
													<td className="border border-gray-300 px-3 py-2">
														{rowActionOptions.length >
															0 && (
															<FormInput
																type="select"
																label={actionsLabel}
																placeholder={selectLabel}
																name="actions"
																value=""
																onChange={(value) =>
																	handleRowAction(
																		variant,
																		String(value)
																	)
																}
																options={rowActionOptions}
																noMargin
															/>
														)}
													</td>
												)}
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
					)}
				</CardBody>
			</Card>

			<Modal
				open={formOpen}
				onClose={closeForm}
				title={StringHelpers.title(
					replaceKeyWithValue(
						editing
							? crud?.pages?.edit?.title || 'Edit :model'
							: crud?.pages?.create?.title || 'Create :model',
						'model',
						crud?.models?.viessmann_appliance_variant ||
							'Serial Prefix'
					)
				)}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				/*
				 * In-flight state only. The shared Modal passes this to the X and Cancel as well as to
				 * Confirm, so gating it on the required fields would leave the user unable to close a form
				 * they had not completed. The required-field check sits on the confirm button instead.
				 */
				disabled={disabled}
				onConfirm={save}
				overriddenConfirmButton={
					<Button
						label={
							disabled
								? crud?.buttons?.submit?.submitting ||
								  'Saving...'
								: crud?.buttons?.submit?.default || 'Save'
						}
						variant={Variants.Primary}
						onClick={save}
						disabled={
							disabled ||
							!values.serial_prefix ||
							!values.installation_type
						}
					/>
				}
				variant={Variants.Primary}
			>
				<FormInput
					type="text"
					name="serial_prefix"
					label={StringHelpers.title(
						fields?.serial_prefix || 'serial prefix'
					)}
					placeholder="7720261"
					/*
					 * 2 to 16 letters or digits, not digits-only. Ten production prefixes are alphanumeric
					 * (TX3A860, VD28047 to VD28055) and one is two characters (24); rejecting them would make
					 * those appliances unregisterable.
					 */
					description={
						fields?.serial_prefix_description ||
						'2 to 16 letters or digits. Most are 7 digits, but some are alphanumeric.'
					}
					value={values.serial_prefix}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							serial_prefix: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.serial_prefix}
				/>

				<FormInput
					type="select"
					name="installation_type"
					label={StringHelpers.title(
						fields?.installation_type || 'installation type'
					)}
					placeholder={StringHelpers.title(
						fields?.please_select || 'please select'
					)}
					value={values.installation_type}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							installation_type: value as string,
						}))
					}
					options={attributes?.installation_types ?? []}
					disabled={disabled}
					errorMessages={errors?.installation_type}
				/>

				<FormInput
					type="text"
					name="gas_safe_model"
					label={StringHelpers.title(
						fields?.gas_safe_model || 'Gas Safe model'
					)}
					/* A real catalogue value, so it reads as a worked example rather than a hint. */
					placeholder="Vitodens"
					description={
						fields?.gas_safe_model_description ||
						'Must match a model name Gas Safe recognises, or installers cannot register this appliance.'
					}
					value={values.gas_safe_model}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							gas_safe_model: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.gas_safe_model}
				/>

				<FormInput
					type="text"
					name="gas_safe_version"
					label={StringHelpers.title(
						fields?.gas_safe_version || 'Gas Safe version'
					)}
					placeholder="100-W"
					value={values.gas_safe_version}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							gas_safe_version: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.gas_safe_version}
				/>

				<FormInput
					type="text"
					name="system_type"
					label={StringHelpers.title(fields?.type || 'type')}
					placeholder="Combi"
					description={
						fields?.system_type_description ||
						'For example Combi, System or Heat Only.'
					}
					value={values.system_type}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							system_type: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.system_type}
				/>

				<FormInput
					type="text"
					name="size"
					label={StringHelpers.title(fields?.size || 'size')}
					placeholder="25"
					value={values.size}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							size: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.size}
					noMargin
				/>
			</Modal>

			<Modal
				open={!!deleting}
				onClose={() => setDeleting(null)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.delete?.title || 'Delete :name?',
						'name',
						crud?.models?.viessmann_appliance_variant ||
							'Serial Prefix'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.delete?.submitting || 'Deleting...'
						: crud?.buttons?.delete?.default || 'Delete'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={remove}
				disabled={disabled}
				variant={Variants.Danger}
			>
				<p className="mb-0">
					{replaceKeyWithValue(
						fields?.delete_prefix_confirm ||
							'Delete serial prefix :name? Serial numbers beginning with it will no longer resolve to this appliance. The change is recorded in the audit log.',
						'name',
						deleting?.serial_prefix || ''
					)}
				</p>
			</Modal>
		</>
	);
}

export default SerialPrefixesSection;
